import { memo, useId } from 'react';
import { createPortal } from 'react-dom';
import { formatToStandardDate } from '../utils';
import type { SessionPreviewFooterProps } from './types';
import Badge from '@/components/atoms/Badge/Badge';
import Icon from '@/components/atoms/Icon';
import TooltipV2 from '@/components/TooltipV2';
import { classnames } from '@/libs/utils';

function SessionPreviewFooter({
  title,
  endTime,
  isCLHomePageEnabled,
  isVideoReadyForPreview,
  contentStatusText,
  newUploadBadge
}: SessionPreviewFooterProps) {
  const analysingTooltipId = useId();
  const showBadge = Boolean(newUploadBadge) || !isVideoReadyForPreview;

  return (
    <div
      className={classnames(
        'flex w-full items-center gap-2 space-x-2 !rounded-b-xl bg-white px-5 py-3.5',
        isCLHomePageEnabled ? '!p-3' : ''
      )}
    >
      <div className="grow truncate">
        <div
          className={classnames('truncate text-left text-sm leading-tight tracking-tight', {
            'mb-[6px] font-medium text-slate-950': isCLHomePageEnabled
          })}
        >
          {title}
        </div>
        <div className="text-left text-xs text-slate-500">{formatToStandardDate(endTime)}</div>
      </div>

      {showBadge && (
        <div className="shrink-0">
          {newUploadBadge ? (
            newUploadBadge.tooltip ? (
              <>
                <div data-tooltip-id={analysingTooltipId}>
                  <Badge variation={newUploadBadge.variation}>
                    {newUploadBadge.iconType === 'spinner' && (
                      <Icon name="IconLoader2" size={12} className="mr-1 animate-spin" />
                    )}
                    {newUploadBadge.text}
                  </Badge>
                </div>
                {createPortal(
                  <TooltipV2
                    tooltipId={analysingTooltipId}
                    tooltipContent={newUploadBadge.tooltip}
                    size="xsmall"
                    colorTheme="light"
                    place="bottom"
                    style={{
                      backgroundColor: '#ffffff',
                      color: '#020617',
                      boxShadow: '0 0 0 1px #e2e8f0, 0 2px 8px 0 rgb(0 0 0 / 0.08)',
                      width: '340px',
                      borderRadius: '8px'
                    }}
                  />,
                  document.body
                )}
              </>
            ) : (
              <Badge variation={newUploadBadge.variation}>{newUploadBadge.text}</Badge>
            )
          ) : (
            <Badge variation="gray">{contentStatusText}</Badge>
          )}
        </div>
      )}
    </div>
  );
}

export default memo(SessionPreviewFooter);
